import React, { useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { getConversations, getAdmin, Conversation } from '../services/messages';
import { verifySession, logout as authLogout, deleteAccount as authDeleteAccount } from '../services/auth';
import ChatView from '../components/ChatView';
import ThemeToggle from '../components/ThemeToggle';
import MessagesDropdown from '../components/MessagesDropdown';
import AccountDropdown from '../components/AccountDropdown';
import DeleteAccountModal from '../components/DeleteAccountModal';
import ProxyImage from '../components/ProxyImage';
import MoodLogoWithTooltip from '../components/MoodLogoWithTooltip';
import './ChatsPage.css';

const ChatsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('user');
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [checkingRole, setCheckingRole] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(undefined);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  const isAdmin = userRole === 'admin';
  const canAccessMessaging = userRole === 'admin' || (userRole === 'editor' && userPermissions.includes('mensajeria'));

  // Verificar rol del usuario
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;

    const checkUserRole = async () => {
      try {

        // Agregar timeout de seguridad de 10 segundos
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutId = setTimeout(() => {

            reject(new Error('Timeout al verificar sesión'));
          }, 10000);
        });

        const sessionPromise = verifySession();
        const result = await Promise.race([sessionPromise, timeoutPromise]);

        clearTimeout(timeoutId);

        if (!isMounted) {

          return;
        }

        if (result.success && result.user) {

          setUserRole(result.user.role || 'user');
          setUserEmail(result.user.email);
          setCurrentUserId(result.user.id);
          setUserPermissions(Array.isArray(result.user.permissions) ? result.user.permissions : []);
        } else {

          // Si no está autenticado, redirigir a home
          if (isMounted) {
            navigate('/');
          }
        }
      } catch (error: any) {

        if (isMounted) {
          // Si hay error, intentar usar datos del localStorage si existen
          try {
            const storedUser = localStorage.getItem('user');
            if (storedUser) {

              const user = JSON.parse(storedUser);
              setUserRole(user.role || 'user');
              setUserEmail(user.email);
              setCurrentUserId(user.id);
            } else {

              navigate('/');
            }
          } catch (localError) {

            navigate('/');
          }
        }
      } finally {
        if (isMounted) {

          setCheckingRole(false);
        }
      }
    };

    checkUserRole();

    return () => {

      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [navigate]);

  // Escuchar eventos de logout desde otras pestañas
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      // Si se detecta un evento de logout en otra pestaña
      if (e.key === 'logoutEvent' && e.newValue) {
        // Cerrar sesión también en esta pestaña
        try {
          setIsLoggingOut(true);

          // Limpiar estado local primero
          setUserRole('user');
          setUserEmail(null);
          setCurrentUserId(undefined);
          setConversations([]);
          setSelectedUserId(null);

          // Limpiar localStorage y sessionStorage
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          localStorage.removeItem('theme');
          sessionStorage.removeItem('oauthParams');
          sessionStorage.removeItem('chatWidget_adminId');

          // Remover tema oscuro
          document.documentElement.classList.remove('dark-theme');
          document.documentElement.classList.add('light-theme');

          // Limpiar parámetros de la URL
          if (window.location.search) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }

          // Verificar que la sesión esté realmente cerrada
          // Si no está cerrada, cerrarla explícitamente
          try {
            const sessionCheck = await verifySession();
            if (sessionCheck.success) {
              // Si todavía hay sesión activa, cerrarla
              await authLogout();
            }
          } catch (sessionError) {
            // Si hay error verificando, asumir que ya está cerrada

          }

          // Redirigir a home
          navigate('/');
        } catch (error) {

          // Aún así, limpiar estado y redirigir
          setUserRole('user');
          setUserEmail(null);
          setCurrentUserId(undefined);
          setConversations([]);
          localStorage.removeItem('user');
          localStorage.removeItem('authToken');
          navigate('/');
        } finally {
          setIsLoggingOut(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);

  // Detectar cambios en el tema
  useEffect(() => {
    const checkTheme = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    };

    // Verificar tema inicial
    checkTheme();

    // Observar cambios en el tema
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Función para manejar logout
  const handleLogout = async () => {
    if (isLoggingOut) {
      return;
    }

    try {
      setIsLoggingOut(true);

      sessionStorage.removeItem('oauthParams');
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());

      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }

      const result = await authLogout();

      // Remover tema oscuro al cerrar sesión
      document.documentElement.classList.remove('dark-theme');
      document.documentElement.classList.add('light-theme');
      localStorage.removeItem('theme');

      // Redirigir a home
      navigate('/');
    } catch (error) {
      // Aún así, redirigir a home
      navigate('/');
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Función para manejar eliminar cuenta
  const [isDeletingAccount, setIsDeletingAccount] = useState<boolean>(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState<boolean>(false);

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) {
      return;
    }

    try {
      setIsDeletingAccount(true);
      
      const result = await authDeleteAccount();
      
      if (result.success) {
        localStorage.clear();
        sessionStorage.clear();
        window.history.replaceState(null, '', window.location.pathname);
        setUserEmail(null);
        setUserRole('user');
        setCurrentUserId(undefined);
        setConversations([]);
        setShowDeleteAccountModal(false);
        navigate('/');
      }
    } catch (error) {
      // Error al eliminar cuenta
    } finally {
      setIsDeletingAccount(false);
    }
  };

  // Función para encontrar el ID del admin (para usuarios normales)
  const findAdminId = async () => {
    try {

      setLoading(true);
      let adminFound = false;

      // Primero intentar obtener el admin directamente
      try {

        const adminResponse = await getAdmin();

        if (adminResponse.success && adminResponse.admin && adminResponse.admin.id) {

          adminFound = true;
          // Establecer ambos estados juntos - React los agrupará y re-renderizará una vez
          setSelectedUserId(adminResponse.admin.id);
          setLoading(false);

          return;
        } else {

        }
      } catch (adminError: any) {

      }

      // Si falla, intentar con conversaciones
      if (!adminFound) {
        try {

          const response = await getConversations();

          const adminConversation = response.conversations.find(conv => conv.role === 'admin');
          if (adminConversation && adminConversation.userId) {

            adminFound = true;
            // Establecer ambos estados juntos - React los agrupará y re-renderizará una vez
            setSelectedUserId(adminConversation.userId);
            setLoading(false);

            return;
          } else {

          }
        } catch (convError: any) {

        }
      }

      // Si no se encontró admin, mostrar error
      if (!adminFound) {

        setError('No se pudo encontrar el administrador. Por favor, intenta más tarde.');
        setLoading(false);
      }
    } catch (error: any) {

      setError('Error al buscar administrador. Por favor, intenta más tarde.');
      setLoading(false);
    }
  };

  // Aplicar asUserId de la URL antes de pintar (useLayoutEffect) para que al abrir /soporte?asUserId=X se vea el chat
  useLayoutEffect(() => {
    if (location.pathname !== '/soporte') return;
    const params = new URLSearchParams(location.search);
    const asUserId = params.get('asUserId');
    if (asUserId) {
      const n = parseInt(asUserId, 10);
      if (!isNaN(n)) {
        setSelectedUserId(n);
        setLoading(false);
        setHasInitializedFromUrl(true);
      }
    } else {
      setSelectedUserId(null);
      setHasInitializedFromUrl(true);
    }
  }, [location.pathname, location.search]);

  // Si algo (p. ej. otro componente) quitó asUserId de la URL, restaurarlo para que el chat siga abierto
  useEffect(() => {
    if (location.pathname !== '/soporte' || selectedUserId == null) return;
    const currentAsUserId = new URLSearchParams(window.location.search).get('asUserId');
    const expected = String(selectedUserId);
    if (currentAsUserId !== expected) {
      navigate(`/soporte?asUserId=${selectedUserId}`, { replace: true });
    }
  }, [location.pathname, location.search, selectedUserId, navigate]);

  // Manejar /soporte para usuarios normales y admin viendo como usuario
  useEffect(() => {

    if (!checkingRole) {
      const isSoporteRoute = window.location.pathname === '/soporte';
      // Editor sin permiso de mensajería no puede acceder a /soporte → redirigir a home
      if (isSoporteRoute && userRole === 'editor' && !canAccessMessaging) {
        navigate('/', { replace: true });
        return;
      }
      // Usar la URL del navegador como fuente de verdad (evita desfase con searchParams al navegar desde otra ruta)
      const asUserIdFromParams = new URLSearchParams(window.location.search).get('asUserId') || searchParams.get('asUserId');

      if (isSoporteRoute) {
        if (canAccessMessaging && asUserIdFromParams) {
          // Admin o editor con mensajería viendo el chat de un usuario específico
          const targetUserId = parseInt(asUserIdFromParams, 10);

          if (!isNaN(targetUserId)) {
            setSelectedUserId(targetUserId);
            setHasInitializedFromUrl(true);
            setLoading(false);
            sessionStorage.removeItem('chatsPage_asUserId');
            // Mantener asUserId en la URL para que el chat específico se conserve (como en el admin)
          }
        } else if (canAccessMessaging && !asUserIdFromParams) {
          // Solo mostrar lista (selectedUserId = null) si la URL realmente no tiene asUserId (evitar sobrescribir por searchParams desactualizado)
          const urlNow = new URLSearchParams(window.location.search).get('asUserId');
          if (!urlNow && !hasInitializedFromUrl) {
            setSelectedUserId(null);
            setHasInitializedFromUrl(true);
          }
          sessionStorage.removeItem('chatsPage_asUserId');
        } else if (!canAccessMessaging && userRole === 'user' && !selectedUserId) {
          // Solo usuario normal (no editor): buscar el adminId para abrir chat con soporte
          setLoading(true);
          findAdminId();
        } else if (!canAccessMessaging && selectedUserId) {
          // Usuario normal con chat seleccionado
        }
      } else if (!canAccessMessaging && !userId && window.location.pathname !== '/soporte') {
        // Sin acceso a mensajería y no está en /soporte ni tiene userId → redirigir a home
        navigate('/');
      }
    } else {

    }
  }, [checkingRole, isAdmin, userId, navigate, searchParams, userRole, canAccessMessaging]);

  // Sincronizar selectedUserId con asUserId de la URL al entrar en /soporte o al cambiar la URL
  useEffect(() => {
    if (!checkingRole && canAccessMessaging && window.location.pathname === '/soporte') {
      const asUserIdFromParams = new URLSearchParams(window.location.search).get('asUserId') || searchParams.get('asUserId');

      if (asUserIdFromParams) {
        const targetUserId = parseInt(asUserIdFromParams, 10);
        if (!isNaN(targetUserId)) {
          setSelectedUserId(targetUserId);
          setLoading(false);
          setHasInitializedFromUrl(true);
        }
        sessionStorage.removeItem('chatsPage_asUserId');
      } else {
        sessionStorage.removeItem('chatsPage_asUserId');
      }
    }
  }, [checkingRole, canAccessMessaging, searchParams]);

  useEffect(() => {

    if (!checkingRole && canAccessMessaging) {
      loadConversations();
    } else if (!checkingRole && !canAccessMessaging) {
      // Si no es admin, verificar si está en /soporte
      const isSoporteRoute = window.location.pathname === '/soporte';

      if (isSoporteRoute) {
        // Si está en /soporte y no hay selectedUserId, asegurar que loading esté en true
        // El findAdminId se ejecutará en el otro useEffect
        if (!selectedUserId) {

        } else {

        }
      } else if (userId) {
        // Si tiene userId en la URL, mostrar el chat individual

      } else {
        // Ya se redirige en el useEffect anterior

      }
    }
  }, [checkingRole, canAccessMessaging, userId, selectedUserId]);

  // Si es admin y está en /chats (no /soporte), asegurar que la URL sea solo /chats
  useEffect(() => {
    if (canAccessMessaging && window.location.pathname === '/chats') {
      const userIdFromUrl = userId ? parseInt(userId, 10) : null;
      const userIdFromQuery = searchParams.get('userId') ? parseInt(searchParams.get('userId') || '', 10) : null;

      // Si hay userId en la URL o query params, redirigir a /soporte
      if (userIdFromUrl || userIdFromQuery) {
        const targetUserId = userIdFromUrl || userIdFromQuery;
        navigate(`/soporte?asUserId=${targetUserId}`, { replace: true });
      } else if (searchParams.get('userId') || userId) {
        // Limpiar cualquier query param o userId de la URL
        navigate('/chats', { replace: true });
      }
    }
  }, [canAccessMessaging, userId, searchParams, navigate]);

  const loadConversations = async () => {
    try {

      setLoading(true);
      setError(null);
      const response = await getConversations();

      setConversations(response.conversations);
    } catch (err: any) {

      setError(err.message || 'Error al cargar conversaciones');
    } finally {

      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Ahora' : `Hace ${minutes} min`;
      }
      return `Hace ${hours}h`;
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return date.toLocaleDateString('es-AR', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Función helper para extraer solo el primer nombre
  const getFirstName = (fullName?: string): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  const handleSelectConversation = (userId: number) => {
    if (canAccessMessaging) {
      sessionStorage.removeItem('chatsPage_asUserId');
      setHasInitializedFromUrl(true);
      setSelectedUserId(userId);
      // Mantener asUserId en la URL para que el chat quede reflejado y no se pierda al recargar
      navigate(`/soporte?asUserId=${userId}`, { replace: true });
    } else {
      // Para usuarios normales: abrir en nueva pestaña
      window.open(`/soporte`, '_blank');
    }
  };

  const handleBackToList = () => {

    setSelectedUserId(null);
    setHasInitializedFromUrl(false);
    sessionStorage.removeItem('chatsPage_asUserId');
    if (canAccessMessaging) {
      navigate('/soporte', { replace: true });
    } else {
      navigate('/soporte');
    }
  };

  // Si tenemos selectedUserId, no mostrar estado de carga aunque loading sea true
  // Esto permite que el ChatView se renderice inmediatamente cuando se encuentra el admin
  const shouldShowLoading = checkingRole || (loading && !selectedUserId);

  if (shouldShowLoading) {

    return (
      <div className={`chats-page${canAccessMessaging ? ' chats-page--no-miniheader' : ''}`}>
        {!canAccessMessaging && (
          <div className="app-miniheader">
            <div className="miniheader-left">
              <a 
                href="/terminos-y-condiciones" 
                className="miniheader-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Términos y Condiciones
              </a>
            </div>
            <div className="miniheader-social">
              <a 
                href="https://smartcityexposantiagodelestero.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="miniheader-link"
              >
                smartcityexposantiagodelestero.com
              </a>
            </div>
          </div>
        )}
        <div className="app-header-bar">
          <div className="header-logo">
            <a 
              href="/" 
              className="header-logo-link"
            >
              <ProxyImage 
                src={isDarkMode 
                  ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                  : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
                } 
                alt="Smart City Expo Santiago del Estero" 
                className="header-logo-image"
              />
            </a>
          </div>
          {/* Logo Mood para mobile - fuera de header-actions */}
          <MoodLogoWithTooltip
            isDarkMode={isDarkMode}
            className="header-mood-logo header-mood-logo-mobile"
          />
          <div className="header-actions">
            <ThemeToggle />
            {userEmail && canAccessMessaging && (
              <MessagesDropdown
                userRole="admin"
                userId={currentUserId}
              />
            )}
            <AccountDropdown
              onLogout={handleLogout}
              onDeleteAccount={() => setShowDeleteAccountModal(true)}
              isLoggingOut={isLoggingOut}
            />
            {/* Logo Mood para tablet/desktop - dentro de header-actions */}
            <MoodLogoWithTooltip
              isDarkMode={isDarkMode}
              className="header-mood-logo header-mood-logo-desktop"
            />
          </div>
        </div>
        <div className="chats-container">
          <div className="chats-loading">Cargando...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`chats-page${canAccessMessaging ? ' chats-page--no-miniheader' : ''}`}>
        {!canAccessMessaging && (
          <div className="app-miniheader">
            <div className="miniheader-left">
              <a 
                href="/terminos-y-condiciones" 
                className="miniheader-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Términos y Condiciones
              </a>
            </div>
            <div className="miniheader-social">
              <a 
                href="https://smartcityexposantiagodelestero.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="miniheader-link"
              >
                smartcityexposantiagodelestero.com
              </a>
            </div>
          </div>
        )}
        <div className="app-header-bar">
          <div className="header-logo">
            <a 
              href="/" 
              className="header-logo-link"
            >
              <ProxyImage 
                src={isDarkMode 
                  ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                  : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
                } 
                alt="Smart City Expo Santiago del Estero" 
                className="header-logo-image"
              />
            </a>
          </div>
          {/* Logo Mood para mobile - fuera de header-actions */}
          <MoodLogoWithTooltip
            isDarkMode={isDarkMode}
            className="header-mood-logo header-mood-logo-mobile"
          />
          <div className="header-actions">
            <ThemeToggle />
            {userEmail && canAccessMessaging && (
              <MessagesDropdown
                userRole="admin"
                userId={currentUserId}
              />
            )}
            <AccountDropdown
              onLogout={handleLogout}
              onDeleteAccount={() => setShowDeleteAccountModal(true)}
              isLoggingOut={isLoggingOut}
            />
            {/* Logo Mood para tablet/desktop - dentro de header-actions */}
            <MoodLogoWithTooltip
              isDarkMode={isDarkMode}
              className="header-mood-logo header-mood-logo-desktop"
            />
          </div>
        </div>
        <div className="chats-container">
          <div className="chats-error">{error}</div>
        </div>
      </div>
    );
  }

  if (!canAccessMessaging && !userId && !selectedUserId && window.location.pathname !== '/soporte') {
    return null;
  }

  return (
    <div className={`chats-page${canAccessMessaging ? ' chats-page--no-miniheader' : ''}`}>
      {!canAccessMessaging && (
        <div className="app-miniheader">
          <div className="miniheader-left">
            <a 
              href="/terminos-y-condiciones" 
              className="miniheader-link"
              target="_blank" 
              rel="noopener noreferrer"
            >
              Términos y Condiciones
            </a>
          </div>
          <div className="miniheader-social">
            <a 
              href="https://smartcityexposantiagodelestero.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="miniheader-link"
            >
              smartcityexposantiagodelestero.com
            </a>
          </div>
        </div>
      )}
      <div className="app-header-bar">
        <div className="header-logo">
          <a 
            href="/" 
            className="header-logo-link"
          >
            <ProxyImage 
              src={isDarkMode 
                ? "https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Logo-SCESDE26-Blanco-e1768488512211.png"
                : "https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png"
              } 
              alt="Smart City Expo Santiago del Estero" 
              className="header-logo-image"
            />
          </a>
        </div>
        {/* Logo Mood para mobile - fuera de header-actions */}
        <a
          href="mailto:contacto@mood-producciones.com"
          target="_blank"
          rel="noopener noreferrer"
          className="header-mood-logo header-mood-logo-mobile"
          title="Contactar a Mood Producciones"
        >
          <ProxyImage
            src={isDarkMode 
              ? 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/logo-nuevo-mood-final-white-e1768878283989.png'
              : 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/logo-mood-color-e1768878022934.png'
            }
            alt="Mood Producciones"
            style={{
              height: isDarkMode ? '28px' : '32px',
              maxWidth: isDarkMode ? '100px' : '120px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </a>
        <div className="header-actions">
          <ThemeToggle />
          {userEmail && canAccessMessaging && (
            <MessagesDropdown
              userRole="admin"
              userId={currentUserId}
            />
          )}
          <AccountDropdown
            onLogout={handleLogout}
            onDeleteAccount={() => setShowDeleteAccountModal(true)}
            isLoggingOut={isLoggingOut}
          />
          {/* Logo Mood para tablet/desktop - dentro de header-actions */}
          <a
            href="mailto:contacto@mood-producciones.com"
            target="_blank"
            rel="noopener noreferrer"
            className="header-mood-logo header-mood-logo-desktop"
            title="Contactar a Mood Producciones"
          >
            <ProxyImage
              src={isDarkMode 
                ? 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/logo-nuevo-mood-final-white-e1768878283989.png'
                : 'https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/logo-mood-color-e1768878022934.png'
              }
              alt="Mood Producciones"
              style={{
                height: isDarkMode ? '28px' : '32px',
                maxWidth: isDarkMode ? '100px' : '120px',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          </a>
        </div>
      </div>
      <div className="chats-container">
        {/* Sidebar con lista de conversaciones para admin o editor con permiso mensajería */}
        {canAccessMessaging && (
          <div className={`chats-sidebar ${selectedUserId ? 'with-chat' : ''}`}>
            <div className="chats-header">
              <h2>Mensajes</h2>
              <button
                type="button"
                className="chats-back-button"
                onClick={() => navigate('/')}
              >
                ← Volver
              </button>
            </div>
            <div className="chats-list">
              {conversations.length === 0 ? (
                <div className="chats-empty">No hay conversaciones</div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.userId}
                    className={`chat-item ${selectedUserId === conv.userId ? 'active' : ''} ${(conv.unreadCount ?? 0) > 0 ? 'unread' : ''}`}
                    onClick={() => handleSelectConversation(conv.userId)}
                  >
                    <div className="chat-item-header">
                      <span className="chat-item-email">
                        {conv.role === 'admin'
                          ? conv.name && conv.name !== conv.email
                            ? `${getFirstName(conv.name)} (Soporte)`
                            : 'Soporte'
                          : conv.name && conv.name !== conv.email
                            ? getFirstName(conv.name)
                            : conv.email}
                      </span>
                      {(conv.unreadCount ?? 0) > 0 && (
                        <span className="chat-item-badge">{conv.unreadCount}</span>
                      )}
                    </div>
                    <div className="chat-item-preview">
                      <span className="chat-item-message">{conv.lastMessage || 'Sin mensajes'}</span>
                      <span className="chat-item-date">{formatDate(conv.lastMessageDate || new Date().toISOString())}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        {(() => {

          if (canAccessMessaging && selectedUserId) {

            return (
              <div className="chats-main">
                <ChatView
                  otherUserId={selectedUserId}
                  onBack={handleBackToList}
                  onMessageSent={loadConversations}
                  currentUserRole="admin"
                  hideBackButton={true}
                  sendAsSupport={userRole === 'editor'}
                />
              </div>
            );
          } else if (canAccessMessaging && !selectedUserId) {

            return (
              <div className="chats-empty-state">
                <p>Selecciona una conversación para comenzar</p>
              </div>
            );
          } else if (selectedUserId) {

            return (
              <div className="chats-main">
                <ChatView
                  otherUserId={selectedUserId}
                  onBack={() => navigate('/')}
                  onMessageSent={undefined}
                  currentUserRole={userRole}
                  hideBackButton={false}
                />
              </div>
            );
          } else if (!isAdmin && window.location.pathname === '/soporte') {

            return (
              <div className="chats-empty-state">
                <p>Cargando conversación con soporte...</p>
              </div>
            );
          } else {

            return null;
          }
        })()}
      </div>
      
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={isDeletingAccount}
      />
    </div>
  );
};

export default ChatsPage;
